import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { FiArrowDown, FiX } from "react-icons/fi";
import { AuthPanel } from "./authPageShell";
import { LoginForm, PasswordResetConfirmForm, PasswordResetForm, RegisterForm } from "./authForms";
import { VerifyEmailStatus } from "./authVerifyEmail";
import StarBackground from "./star";
import { useAuth } from "./authContext";
import { t, useI18n } from "./utils/i18n";
import { getLastModulePath } from "./utils/lastModule";
import { APP_VERSION } from "./utils/appVersion";
import LanguageSwitch from "./LanguageSwitch";
import HomeBackground from "./home/HomeBackground";
import HomeChapterDivider from "./home/HomeChapterDivider";
import HomeHero from "./home/HomeHero";
import HomeToolList from "./home/HomeToolList";
import useHomeSerifFont from "./home/useHomeSerifFont";
import { getScene } from "./scene/scene";

// Lets the page's CSS pick the right CJK serif with :lang().
const HOME_LANG = { zh: "zh-Hant", en: "en", ja: "ja" };

// Closes only from the X button or Escape: a stray click on the backdrop must
// not throw away a half-filled form. "verify-email" and "reset-confirm" are
// opened by the emailed links (/verify-email, /reset-password).
function AuthModal({ mode, setMode, onClose, onLoginSuccess }) {
  const [searchParams] = useSearchParams();
  useEffect(() => {
    if (!mode) return undefined;
    const handleKeyDown = (event) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [mode, onClose]);

  if (!mode) return null;

  const renderForm = () => {
    if (mode === "register") return <RegisterForm onSwitchMode={setMode} />;
    if (mode === "reset") return <PasswordResetForm onSwitchMode={setMode} />;
    if (mode === "verify-email") {
      return (
        <VerifyEmailStatus
          email={searchParams.get("email") || ""}
          token={searchParams.get("token") || ""}
          onSwitchMode={setMode}
        />
      );
    }
    if (mode === "reset-confirm") {
      return <PasswordResetConfirmForm token={searchParams.get("token") || ""} onSwitchMode={setMode} />;
    }
    return <LoginForm onSwitchMode={setMode} onSuccess={onLoginSuccess} />;
  };

  return (
    <div className="auth-modal-backdrop" role="dialog" aria-modal="true">
      <div className="auth-modal">
        <button type="button" onClick={onClose} className="auth-modal-close" aria-label={t("auth.common.close")}>
          <FiX size={20} />
        </button>
        <AuthPanel>{renderForm()}</AuthPanel>
      </div>
    </div>
  );
}

// `initialAuthMode` opens the auth modal straight away; the emailed-link routes
// use it so their result shows over the home page like every other auth step.
function HomePage({ initialAuthMode = null }) {
  const [authMode, setAuthMode] = useState(initialAuthMode);
  // Where to land after a login triggered from a tool CTA (null = last module).
  const [pendingToolPath, setPendingToolPath] = useState(null);
  const { isAuthenticated } = useAuth();
  const { language: currentLanguage } = useI18n();
  const navigate = useNavigate();
  const toolsSectionRef = useRef(null);
  const scene = getScene();
  const translate = (key) => t(key, currentLanguage);
  useHomeSerifFont(currentLanguage);

  const goHome = () => {
    navigate("/");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // Signed-in users go straight in; signed-out users get the login modal and
  // land on the tool they asked for once it succeeds.
  const openTool = (path) => {
    if (isAuthenticated) {
      navigate(path);
    } else {
      setPendingToolPath(path);
      setAuthMode("login");
    }
  };

  const handleStart = () => {
    if (isAuthenticated) {
      navigate(getLastModulePath());
    } else {
      setPendingToolPath(null);
      setAuthMode("login");
    }
  };

  const scrollToTools = () => {
    toolsSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className="home-page" data-scene={scene} lang={HOME_LANG[currentLanguage]}>
      <style>{`
        .home-page {
          --home-bg-veil: linear-gradient(180deg, rgba(4,6,18,0.22), rgba(4,6,18,0.66));
          /* Display serif for the hero and the tool volumes (useHomeSerifFont). */
          --home-serif: "Cormorant Garamond", "Noto Serif TC", "Songti TC", Georgia, serif;
          --home-band: rgba(1,4,11,0.58);
          min-height: 100vh;
          background: #01040b;
          color: #f8fafc;
          position: relative;
          overflow: hidden auto;
        }
        .home-bg,
        .home-bg::after {
          position: fixed;
          inset: 0;
          content: "";
          pointer-events: none;
        }
        .home-bg {
          background-size: cover;
          background-position: center;
          opacity: 0.38;
        }
        .home-bg::after {
          background: var(--home-bg-veil);
        }
        .home-page:lang(ja) {
          --home-serif: "Cormorant Garamond", "Noto Serif JP", "Hiragino Mincho ProN", "Yu Mincho", Georgia, serif;
        }
        .home-page[data-scene="day"] {
          --home-bg-veil: linear-gradient(180deg, rgba(14,6,14,0.22), rgba(12,6,14,0.66));
          --home-band: rgba(6,3,10,0.58);
          background: #06030a;
        }
        .home-shell {
          width: min(1160px, calc(100% - 2rem));
          margin: 0 auto;
          position: relative;
          z-index: 10;
        }
        .home-nav {
          min-height: 5.6rem;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 1rem;
        }
        .home-brand {
          display: inline-flex;
          align-items: center;
          border: 0;
          padding: 0;
          cursor: pointer;
          color: #f8fafc;
          background: transparent;
          font-size: 1.05rem;
          font-weight: 900;
          letter-spacing: 0.16em;
          text-transform: uppercase;
          transition: color 160ms ease, transform 160ms ease;
        }
        .home-brand:hover {
          color: #bae6fd;
          transform: translateY(-1px);
        }
        .home-nav-actions {
          display: flex;
          align-items: center;
          justify-content: flex-end;
          gap: 0.35rem;
          flex-wrap: wrap;
        }
        .nav-link-button {
          border: 0;
          cursor: pointer;
          color: #cbd5e1;
          background: transparent;
          font-weight: 600;
          padding: 0.62rem 0.8rem;
          transition: color 160ms ease;
        }
        .nav-link-button:hover {
          color: #f8fafc;
        }
        /* The nav's one bordered action: sign up, or continue when signed in. */
        .home-nav-pill {
          margin-left: 0.4rem;
          border: 1px solid rgba(226,232,240,0.3);
          border-radius: 999px;
          padding: 0.55rem 1.15rem;
          cursor: pointer;
          color: #f8fafc;
          background: transparent;
          font-weight: 600;
          transition: border-color 160ms ease, background-color 160ms ease;
        }
        .home-nav-pill:hover {
          border-color: rgba(226,232,240,0.6);
          background: rgba(226,232,240,0.06);
        }
        .nav-link-button:focus-visible,
        .home-nav-pill:focus-visible {
          outline: 2px solid #8fd3ff;
          outline-offset: 3px;
        }
        .tools {
          position: relative;
          padding: 1.5rem 0 5rem;
        }
        /* A full-width darker band behind the second chapter, fading in under
           the chapter mark and back out above the footer. */
        .tools::before {
          content: "";
          position: absolute;
          z-index: -1;
          top: 0;
          bottom: 0;
          left: 50%;
          width: 100vw;
          transform: translateX(-50%);
          pointer-events: none;
          background: linear-gradient(180deg, transparent, var(--home-band) 16rem, var(--home-band) calc(100% - 8rem), transparent);
        }
        .auth-modal-backdrop {
          position: fixed;
          inset: 0;
          z-index: 50;
          display: flex;
          flex-direction: column;
          padding: 2rem 1rem;
          overflow-y: auto;
          overscroll-behavior: contain;
          /* Auth fills follow the scene (src/scene/brandSurfaces.css). */
          background: var(--brand-overlay);
        }
        .auth-modal {
          width: min(480px, 100%);
          position: relative;
          /* Centred while it fits; a taller form scrolls the backdrop from its top. */
          margin: auto;
          /* An opaque base under the translucent panel, instead of blurring the page behind it. */
          border-radius: var(--r-lg);
          background: var(--brand-modal);
        }
        .auth-modal-close {
          position: absolute;
          top: -0.85rem;
          right: -0.85rem;
          width: 2.4rem;
          height: 2.4rem;
          border-radius: 50%;
          border: 1px solid rgba(226,232,240,0.2);
          background: var(--brand-overlay);
          color: #f8fafc;
          display: grid;
          place-items: center;
          cursor: pointer;
          z-index: 2;
        }
        .home-footer {
          display: flex;
          justify-content: center;
          gap: 1rem;
          flex-wrap: wrap;
          padding: 2rem 0 2.5rem;
          color: rgba(226,232,240,0.76);
          font-size: 0.9rem;
        }
        .home-footer a {
          color: #bae6fd;
          font-weight: 800;
          text-decoration: none;
        }
        @media (max-width: 880px) {
          .home-nav {
            align-items: flex-start;
            flex-direction: column;
            padding: 1rem 0;
          }
          .home-nav-actions {
            justify-content: flex-start;
          }
        }
      `}</style>
      <HomeBackground scene={scene} />
      {/* Dusk keeps only a few early stars. */}
      <StarBackground starCount={scene === "day" ? 50 : 140} isLoading={false} containerStyle={{ zIndex: 1, opacity: 0.44 }} />
      <div className="home-shell">
        <nav className="home-nav" aria-label="JolyMei">
          <button type="button" className="home-brand" onClick={goHome}>
            JolyMei
          </button>
          <div className="home-nav-actions">
            <LanguageSwitch variant="plain" />
            {isAuthenticated ? (
              <button type="button" className="home-nav-pill" onClick={() => navigate(getLastModulePath())}>
                {translate("home.nav.enter")}
              </button>
            ) : (
              <>
                <button type="button" className="nav-link-button" onClick={() => setAuthMode("login")}>
                  {translate("home.nav.login")}
                </button>
                <button type="button" className="home-nav-pill" onClick={() => setAuthMode("register")}>
                  {translate("home.nav.signup")}
                </button>
              </>
            )}
          </div>
        </nav>

        <main>
          <HomeHero
            scene={scene}
            title={translate("home.hero.title")}
            subtitle={translate("home.hero.subtitle")}
          >
            <button type="button" className="home-hero-cta" onClick={handleStart}>
              {translate("home.cta.start")}
            </button>
            <button type="button" className="home-hero-more" onClick={scrollToTools}>
              <span>{translate("home.cta.grimoire")}</span>
              <FiArrowDown aria-hidden="true" />
            </button>
          </HomeHero>

          <section className="tools" ref={toolsSectionRef} aria-labelledby="home-tools-title">
            <HomeChapterDivider id="home-tools-title" title={translate("home.tools.heading")} />
            <HomeToolList onOpen={openTool} />
          </section>
        </main>
        <footer className="home-footer">
          <span>JolyMei{APP_VERSION ? ` ${APP_VERSION}` : ''}</span>
          <Link to="/privacy">{translate("legal.privacy")}</Link>
          <Link to="/terms">{translate("legal.terms")}</Link>
        </footer>
      </div>
      <AuthModal
        mode={authMode}
        setMode={setAuthMode}
        onClose={() => {
          setAuthMode(null);
          // Leave the link route so its token is not kept in the address bar.
          if (initialAuthMode) navigate("/", { replace: true });
        }}
        onLoginSuccess={() => navigate(pendingToolPath || getLastModulePath())}
      />
    </div>
  );
}

export default HomePage;
